import { useNavigate } from 'react-router-dom'
import { CreateRequestSelectLocationsDocument, useAllBranchesQuery } from '@/buyers/_gen/gql'
import LocationModal from '@/buyers/components/LocationModal'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import useSession from '@/buyers/hooks/useSession'
import Action from '@/gf/components/Action'
import Box from '@/gf/components/Box'
import useToggle from '@/gf/hooks/useToggle'
import AddressM from '@/gf/modules/Address'
import { Point } from '@/types'
import LocationsList from './SelectLocationStep/LocationsList'
import StartOverButton from './StartOverButton'
import StickyBottomBar from './StickyBottomBar'
import { PartialRequestUpdate } from './types'
import { useEffect, useMemo } from 'react'

const SelectLocationStep = ({
  reset,
  request,
  updateRequest,
  onBackClicked,
  onLocationChanged,
  onSaveWithoutVendors,
  saveWithoutVendorsInProgress,
  orgHasLocations = true,
  userLocationGroups = [],
  orgLocationGroups = [],
}: {
  reset?: () => void
  onBackClicked: () => void
  onLocationChanged: (point: Point, selectedLocationId?: string) => void
  onSaveWithoutVendors?: () => void
  saveWithoutVendorsInProgress?: boolean
  orgHasLocations?: boolean
  userLocationGroups?: Array<{ id: string; name: string }>
  orgLocationGroups?: Array<{ id: string; name: string }>
} & PartialRequestUpdate) => {
  const navigate = useNavigate()
  const client = useGqlClient()
  const { organization } = useSession()
  const [locationModalOpen, locationModalToggle] = useToggle()
  const branches = useAllBranchesQuery({ variables: { value: '' }, client }).data?.allBranches

  // Merge and sort location groups: user groups first, then remaining org groups
  const sortedLocationGroups = useMemo(() => {
    const userGroupIds = new Set(userLocationGroups.map((g) => g.id))
    const remainingOrgGroups = orgLocationGroups.filter((g) => !userGroupIds.has(g.id))
    return [...userLocationGroups, ...remainingOrgGroups]
  }, [userLocationGroups, orgLocationGroups])

  // Auto-select first user group (or first org group if no user groups)
  useEffect(() => {
    // Skip if user has already selected a location or if no groups available
    if (request.locationId || sortedLocationGroups.length === 0) return
    // Auto-select first group if none selected
    if (!request.locationGroupId) {
      updateRequest({ locationGroupId: sortedLocationGroups[0].id })
    }
  }, [request.locationGroupId, request.locationId, sortedLocationGroups, updateRequest])

  const onNext = () => {
    // TODO: this is only if the order type selection is available
    navigate('../order-type')
  }

  return (
    <>
      <LocationModal
        open={locationModalOpen}
        refetchQueries={[CreateRequestSelectLocationsDocument]}
        onClose={locationModalToggle.off}
        onComplete={(locationId, variables) =>
          onLocationChanged(variables.address?.point as Point, locationId)
        }
        branches={(organization.requireBillingCompany ? branches : []) ?? []}
        buyers={[]}
        initialAddress={AddressM.init()}
        showPersist
      />

      <Box className="shadow-base rounded-xl px-6 py-8">
        <div className="space-y-6 max-w-screen-md mx-auto">
          <h4 className="text-xl font-medium text-gray-900">
            What location do you need the parts for?
          </h4>

          {sortedLocationGroups.length > 0 && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Location Group</label>
              <select
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                value={request.locationGroupId ?? ''}
                onChange={(e) => {
                  const groupId = e.target.value || undefined
                  updateRequest({
                    locationGroupId: groupId,
                    locationId: groupId ? undefined : request.locationId,
                  })
                }}
              >
                {sortedLocationGroups.map((group, index) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                    {index < userLocationGroups.length ? ' (Your Group)' : ''}
                  </option>
                ))}
              </select>
              <p className="text-sm text-gray-500">Required for your requests</p>
            </div>
          )}

          <LocationsList
            onLocationSelected={(point, locationId) =>
              updateRequest({
                nearbyReferencePoint: point,
                locationId,
              })
            }
            selectedLocationId={request.locationId ?? null}
            onAddLocationClicked={locationModalToggle.on}
            orgHasLocations={orgHasLocations}
            orgMachineId={request.machineOrgId}
          />
        </div>
      </Box>

      <StickyBottomBar>
        {reset && <StartOverButton reset={reset} />}

        <div className="flex-grow" />

        {onBackClicked && <Action.S onClick={onBackClicked}>Previous</Action.S>}

        {onSaveWithoutVendors && (
          <Action.S
            onClick={onSaveWithoutVendors}
            disabled={
              saveWithoutVendorsInProgress || (!request.locationId && !request.locationGroupId)
            }
            performing={saveWithoutVendorsInProgress}
          >
            Save without Vendors
          </Action.S>
        )}

        <Action.P
          color="blue"
          onClick={() => onNext()}
          disabled={!request.locationId && !request.locationGroupId}
        >
          Save and Select Vendors
        </Action.P>
      </StickyBottomBar>
    </>
  )
}

export default SelectLocationStep
